import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"

import Column from "../../../common/components/Column"
import {
  ADDITIONAL_OFFICE_TIERS,
  EXTENDED_ADDITIONAL_OFFICES_FIELD,
  clearedOfficePatchFromTier,
  isIntakeCheckboxTrue,
} from "./additionalOfficeIntakeFields"
import { recruitProfileNestedPanelSx } from "./recruitProfileSectionStyles"

/**
 * @param {object} props
 * @param {object} props.form
 * @param {function} props.patchForm
 * @param {function} props.setFieldErrors
 * @param {boolean} props.disabled
 * @param {object} props.fieldErrors
 * @param {function} props.renderTextFields
 */
export default function AdditionalOfficesFormSection({
  form,
  patchForm,
  setFieldErrors,
  disabled,
  fieldErrors,
  renderTextFields,
}) {
  const theme = useTheme()
  const panelSx = recruitProfileNestedPanelSx(theme)

  function handleOfficeCheckbox(tierIndex, checked) {
    if (checked) {
      patchForm({ [ADDITIONAL_OFFICE_TIERS[tierIndex].checkboxKey]: true })
      return
    }
    const cleared = clearedOfficePatchFromTier(tierIndex)
    patchForm(cleared)
    setFieldErrors((prev) => {
      const next = { ...prev }
      for (const key of Object.keys(cleared)) {
        delete next[key]
      }
      return next
    })
  }

  function handleFourPlusChange(checked) {
    if (checked) {
      patchForm({ fourPlusOffices: true })
      return
    }
    patchForm({ fourPlusOffices: false, additionalOffices: "" })
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next.additionalOffices
      return next
    })
  }

  const showSecondCheckbox = isIntakeCheckboxTrue(form.additionalOffice)
  const showThirdCheckbox = isIntakeCheckboxTrue(form.secondAdditionalOffice)
  const showFourPlusCheckbox = isIntakeCheckboxTrue(form.thirdAdditionalOffice)

  function renderTierPanel(tierIndex) {
    const tier = ADDITIONAL_OFFICE_TIERS[tierIndex]
    const checked = isIntakeCheckboxTrue(form[tier.checkboxKey])

    return (
      <Box key={tier.checkboxKey} sx={panelSx}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              disabled={disabled}
              onChange={(e) =>
                handleOfficeCheckbox(tierIndex, e.target.checked)
              }
            />
          }
          label={
            <Typography variant="body2" fontWeight={700}>
              {tier.checkboxLabel}
            </Typography>
          }
          sx={{ m: 0, mb: checked ? 1.5 : 0 }}
        />
        {checked ? (
          <Box sx={{ mt: 0.5, width: "100%" }}>
            {renderTextFields(
              tier.fields,
              form,
              patchForm,
              disabled,
              fieldErrors
            )}
          </Box>
        ) : null}
      </Box>
    )
  }

  return (
    <Column sx={{ gap: 1.25, alignItems: "stretch" }}>
      {renderTierPanel(0)}

      {showSecondCheckbox ? renderTierPanel(1) : null}

      {showThirdCheckbox ? renderTierPanel(2) : null}

      {showFourPlusCheckbox ? (
        <Box sx={panelSx}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isIntakeCheckboxTrue(form.fourPlusOffices)}
                disabled={disabled}
                onChange={(e) => handleFourPlusChange(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" fontWeight={700}>
                4+ Offices?
              </Typography>
            }
            sx={{
              m: 0,
              mb: isIntakeCheckboxTrue(form.fourPlusOffices) ? 1.5 : 0,
            }}
          />
          {isIntakeCheckboxTrue(form.fourPlusOffices) ? (
            <Box sx={{ mt: 0.5, width: "100%" }}>
              {renderTextFields(
                [EXTENDED_ADDITIONAL_OFFICES_FIELD],
                form,
                patchForm,
                disabled,
                fieldErrors
              )}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Column>
  )
}
